import { useState } from "react";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function KeyFieldPanel({ settings, onSave, onClear }) {
  const [openai, setOpenai] = useState("");
  const [anthropic, setAnthropic] = useState("");
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {};
    if (openai !== "") payload.openai_api_key = openai;
    if (anthropic !== "") payload.anthropic_api_key = anthropic;
    if (Object.keys(payload).length === 0) {
      toast("Nothing to save", { description: "Enter a key to update." });
      return;
    }
    setSaving(true);
    try {
      await onSave(payload);
      setOpenai("");
      setAnthropic("");
      toast.success("Keys saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save keys");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-white border-2 border-[#1A1A1A] shadow-[5px_5px_0px_#1A1A1A] p-6 mb-10"
      data-testid="admin-settings-section"
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl font-semibold mb-1">
            LLM keys
          </h2>
          <p className="font-sans-ui text-sm text-[#6B6A68]">
            Store your own Anthropic or OpenAI key. Leave empty to use the{" "}
            <span className="italic">Emergent universal key</span> fallback.
          </p>
        </div>
        {settings && (
          <div className="flex items-center gap-2 font-sans-ui text-[10px] uppercase tracking-[0.2em] font-semibold">
            <span
              className={`px-2 py-1 border-2 ${
                settings.emergent_fallback_available
                  ? "border-[#0F5132] text-[#0F5132] bg-[#D1E7DD]"
                  : "border-[#842029] text-[#842029] bg-[#F8D7DA]"
              }`}
            >
              Emergent fallback: {settings.emergent_fallback_available ? "on" : "off"}
            </span>
          </div>
        )}
      </div>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KeyField
          label="Anthropic API key"
          placeholder="sk-ant-..."
          stored={settings?.has_anthropic_key}
          value={anthropic}
          onChange={setAnthropic}
          onClear={() => onClear("anthropic")}
          testid="admin-anthropic-key"
        />
        <KeyField
          label="OpenAI API key"
          placeholder="sk-..."
          stored={settings?.has_openai_key}
          value={openai}
          onChange={setOpenai}
          onClear={() => onClear("openai")}
          testid="admin-openai-key"
        />
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={saving || (!openai && !anthropic)}
            data-testid="admin-save-keys"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] text-[#F4F1EA] border-2 border-[#1A1A1A] font-sans-ui font-bold uppercase tracking-[0.15em] text-xs shadow-[3px_3px_0px_#EB5E28] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#EB5E28] active:translate-y-0 active:shadow-[0px_0px_0px_#EB5E28] transition-all disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Save keys
          </button>
        </div>
      </form>
    </section>
  );
}

function KeyField({ label, placeholder, stored, value, onChange, onClear, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#6B6A68]">
          {label}
        </label>
        <span
          className={`font-sans-ui text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 border ${
            stored
              ? "border-[#0F5132] text-[#0F5132] bg-[#D1E7DD]"
              : "border-[#6B6A68] text-[#6B6A68] bg-[#F4F1EA]"
          }`}
          data-testid={`${testid}-status`}
        >
          {stored ? "Stored" : "Not set"}
        </span>
      </div>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={stored ? "•••••••• (enter new to replace)" : placeholder}
          autoComplete="off"
          data-testid={`${testid}-input`}
          className="w-full border-2 border-[#1A1A1A] bg-[#F4F1EA] px-3 py-2.5 pr-20 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#EB5E28]"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide" : "Show"}
          className="absolute right-10 top-1/2 -translate-y-1/2 p-1.5 text-[#6B6A68] hover:text-[#1A1A1A] font-sans-ui text-[10px] uppercase tracking-[0.15em] font-semibold"
        >
          {show ? "Hide" : "Show"}
        </button>
        {stored && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear stored key"
            data-testid={`${testid}-clear`}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#842029] hover:bg-[#F8D7DA] rounded-sm"
            title="Remove stored key"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
